import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './TicketEditPanel.css';

const API_URL = process.env.REACT_APP_API_URL;

/**
 * Edit and reassign a ticket. Shown to admins and approvers only.
 *
 * Every field posts through PUT /api/tickets/:id, which until now echoed the
 * request back without writing anything - so an edit appeared to work and was
 * gone on the next refresh.
 */
function TicketEditPanel({ ticket, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: ticket.title || '',
    description: ticket.description || '',
    status_id: ticket.status_id || '',
    priority_id: ticket.priority_id || '',
    category_id: ticket.category_id || '',
    department_id: ticket.department_id || '',
    assigned_to: ticket.assigned_to || '',
    contractor_id: ticket.contractor_id || ''
  });
  const [ref, setRef] = useState({ statuses: [], priorities: [], categories: [], departments: [], users: [] });
  const [contractors, setContractors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadReference = useCallback(async () => {
    try {
      const [refRes, conRes] = await Promise.all([
        axios.get(`${API_URL}/reference-data`),
        axios.get(`${API_URL}/contractors`).catch(() => ({ data: [] }))
      ]);
      const d = refRes.data || {};
      setRef({
        statuses:    Array.isArray(d.statuses)    ? d.statuses    : [],
        priorities:  Array.isArray(d.priorities)  ? d.priorities  : [],
        categories:  Array.isArray(d.categories)  ? d.categories  : [],
        departments: Array.isArray(d.departments) ? d.departments : [],
        users:       Array.isArray(d.users)       ? d.users       : []
      });
      const list = Array.isArray(conRes.data) ? conRes.data : (conRes.data?.contractors || []);
      setContractors(list.filter((c) => c.availability_status !== 'inactive'));
    } catch (e) {
      setError('Could not load the dropdown options. You can still edit the text fields.');
    }
  }, []);

  useEffect(() => { loadReference(); }, [loadReference]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('A ticket needs a title.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Send only what changed, and turn a cleared select into an explicit
      // null so "unassign" works rather than being dropped as falsy.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        const before = ticket[k] == null ? '' : String(ticket[k]);
        const now = v == null ? '' : String(v);
        if (before === now) return;
        payload[k] = now === '' ? null : v;
      });
      if (Object.keys(payload).length === 0) {
        onCancel();
        return;
      }
      const res = await axios.put(`${API_URL}/tickets/${ticket.id}`, payload);
      onSaved(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save those changes.');
    } finally {
      setSaving(false);
    }
  };

  const Select = ({ label, field, options, valueKey = 'id', labelKey = 'name', allowNone }) => (
    <div className="tep-field">
      <label htmlFor={`tep-${field}`}>{label}</label>
      <select id={`tep-${field}`} value={form[field] ?? ''} onChange={set(field)}>
        {allowNone && <option value="">— Unassigned —</option>}
        {!allowNone && !form[field] && <option value="">— Select —</option>}
        {options.map((o) => (
          <option key={o[valueKey]} value={o[valueKey]}>{o[labelKey]}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="ticket-edit-panel">
      <h3 className="tep-title">Edit ticket</h3>

      {error && <div className="tep-error" role="alert">{error}</div>}

      <div className="tep-field tep-full">
        <label htmlFor="tep-title">Title</label>
        <input id="tep-title" type="text" value={form.title} onChange={set('title')} />
      </div>

      <div className="tep-field tep-full">
        <label htmlFor="tep-description">Description</label>
        <textarea id="tep-description" rows={5} value={form.description} onChange={set('description')} />
      </div>

      <div className="tep-grid">
        <Select label="Status"     field="status_id"     options={ref.statuses} />
        <Select label="Priority"   field="priority_id"   options={ref.priorities} />
        <Select label="Category"   field="category_id"   options={ref.categories} />
        <Select label="Department" field="department_id" options={ref.departments} />
        <Select label="Assigned to" field="assigned_to"  options={ref.users} allowNone />
        <Select label="Contractor"  field="contractor_id" options={contractors} allowNone />
      </div>

      <div className="tep-actions">
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
      </div>
    </div>
  );
}

export default TicketEditPanel;
